/**
 * Shared hook for building a driver lookup map from partner users.
 *
 * Used by Incoming and Dispatch pages. Backed by React Query's
 * usePartnerUsersQuery cache, so the driver list is fetched once
 * and shared across both pages.
 */

import { useMemo, useCallback } from 'react';
import { PartnerUser } from '../types/logistics';

export function useDriverMap(partnerUsers: PartnerUser[] | undefined) {
  const driverMap = useMemo(() => {
    const map = new Map<number, PartnerUser>();
    if (partnerUsers) {
      partnerUsers.filter(u => u.role === 'DRIVER' && u.isActive).forEach(d => map.set(d.id, d));
    }
    return map;
  }, [partnerUsers]);

  const getDriverName = useCallback((driverId: number | null): string => {
    if (driverId === null) return 'Unassigned';
    return driverMap.get(driverId)?.name || `Driver #${driverId}`;
  }, [driverMap]);

  return { driverMap, getDriverName };
}
